export type UserMode = 'high_school' | 'university';
export type ConfidenceLevel = 'struggling' | 'on_track' | 'confident';
export type SessionFormat = 'short' | 'standard' | 'deep';
export type DayOfWeek =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export interface ParentProfile {
  parent_id: string;
  email: string;
  display_name: string;
  consent_version: string;
  created_at: string;
}

export interface Child {
  child_id: string;
  display_name: string;
  mode: UserMode;
  school_year: string;
  country?: string;
  education_authority?: string; // deprecated — use education_authorities
  education_authorities?: string[];
  specializations?: string[];
  date_of_birth?: string;
  streak_count: number;
  last_study_date?: string;
  created_at: string;
}

export interface CurriculumInfo {
  country: string;
  authority: string;
  school_year: string;
  subjects: string[];
  official_url: string;
  fetched_at: string;
  status: 'live' | 'placeholder';
}

export interface CalendarPeriod {
  name: string;
  start: string;
  end: string;
}

export interface CalendarHoliday extends CalendarPeriod {
  zone: 'A' | 'B' | 'C' | null;
}

export interface SchoolCalendar {
  country: string;
  academic_year: string;
  trimesters: CalendarPeriod[];
  holidays: CalendarHoliday[];
  national_exam_windows: CalendarPeriod[];
  official_url: string;
  fetched_at: string;
  status: 'live' | 'placeholder' | 'pending';
  cache_hit: boolean;
}

export interface AddChildResponse {
  child_id: string;
  display_name: string;
  join_code: string;
  join_code_expires_at: string;
}

export interface TimetableEntry {
  subject: string;
  day: DayOfWeek;
  start_time: string;
  end_time: string;
  room?: string | null;
}

export interface FreeTimeSlot {
  day: DayOfWeek;
  start_time: string;
  end_time: string;
  label?: string | null;
}

export interface AiSummary {
  child_id: string;
  suggestion: string;
  generated_at: string;
}

export interface Proposal {
  subject: string;
  confidence: ConfidenceLevel;
  format: SessionFormat;
  duration_minutes: number;
  rationale: string;
  free_time_slot: string | null;
  type?: 'study' | 'homework';
  homework_id?: string;
}

export interface ProposalsResponse {
  proposals: Proposal[];
  degraded?: boolean;
}

export interface HomeworkExercise {
  exercise_id: string;
  prompt: string;
  status: 'pending' | 'done';
}

export interface Homework {
  homework_id: string;
  subject: string;
  themes: string[];
  exercises: HomeworkExercise[];
  status: 'open' | 'complete';
  ocr_status?: 'not_needed' | 'pending' | 'done' | 'failed';
  created_at: string;
  has_document?: boolean;
}

export interface StudySession {
  pk: string;
  session_id: string;
  subject: string;
  topic: string;
  format: SessionFormat;
  duration_minutes: number;
  note: string | null;
  created_at: string;
}

export const DAYS_OF_WEEK: DayOfWeek[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const CONFIDENCE_LEVELS: ConfidenceLevel[] = ['struggling', 'on_track', 'confident'];

export const CURRENT_CONSENT_VERSION = '1.0';
